import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useCartStore } from '../store/cartStore'
import { useOrdersStore } from '../store/ordersStore'
import { useAuthStore } from '../store/authStore'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../components/ui/SafeIcon'

const { FiArrowLeft, FiCheckCircle, FiTruck, FiCreditCard, FiBuilding } = FiIcons

function CheckoutPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { items, getTotalPrice, clearCart } = useCartStore()
  const { createOrder } = useOrdersStore()
  
  const [currentStep, setCurrentStep] = useState(1)
  const [isProcessing, setIsProcessing] = useState(false)

  // Form state
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    vatId: '',
    contactPerson: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    zipCode: '',
    country: 'Deutschland'
  })

  const [shippingMethod, setShippingMethod] = useState('express')
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer')

  const total = getTotalPrice()
  const shippingCost = shippingMethod === 'express' ? 25 : 15
  const finalTotal = total + shippingCost

  const handleCompanyInfoChange = (field, value) => {
    setCompanyInfo(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsProcessing(true)

    try {
      // Create order
      const order = createOrder({
        userId: user?.id,
        buyer: companyInfo,
        shipping: {
          method: shippingMethod,
          cost: shippingCost,
          estimatedDelivery: shippingMethod === 'express' ? '5-10 Tage' : '15-30 Tage'
        },
        payment: {
          method: paymentMethod,
          status: 'pending'
        },
        items: items.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
          supplier: item.supplier
        })),
        totals: {
          subtotal: total,
          shipping: shippingCost,
          total: finalTotal
        },
        status: 'confirmed'
      })

      // Clear cart
      clearCart()

      // Navigate to success page
      navigate(`/order-success/${order.id}`)
    } catch (error) {
      console.error('Order creation failed:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  const steps = [
    { number: 1, title: t('checkout.step1'), icon: FiBuilding },
    { number: 2, title: t('checkout.step2'), icon: FiTruck },
    { number: 3, title: t('checkout.step3'), icon: FiCreditCard },
    { number: 4, title: t('checkout.step4'), icon: FiCheckCircle }
  ]

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Ihr Warenkorb ist leer</h2>
          <button 
            onClick={() => navigate('/search')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Weiter einkaufen
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Kasse - Bestellung abschließen | 1688Euro.com</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-6">
              <button 
                onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : navigate('/cart')}
                className="flex items-center text-gray-600 hover:text-gray-800"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2" />
                Zurück
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Bestellung abschließen</h1>
              <div className="w-10"></div>
            </div>

            <div className="flex justify-between mb-4">
              {steps.map((step, index) => (
                <div key={step.number} className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    currentStep >= step.number 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'border-gray-300 text-gray-400'
                  }`}>
                    {currentStep > step.number ? (
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5" />
                    ) : (
                      <SafeIcon icon={step.icon} className="w-5 h-5" />
                    )}
                  </div>
                  <span className="text-sm mt-2 text-gray-600">{step.title}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Company Information */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold mb-4">Firmeninformationen</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Firmenname *
                      </label>
                      <input
                        type="text"
                        required
                        value={companyInfo.name}
                        onChange={(e) => handleCompanyInfoChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        USt-IDNr.
                      </label>
                      <input
                        type="text"
                        value={companyInfo.vatId}
                        onChange={(e) => handleCompanyInfoChange('vatId', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ansprechpartner *
                      </label>
                      <input
                        type="text"
                        required
                        value={companyInfo.contactPerson}
                        onChange={(e) => handleCompanyInfoChange('contactPerson', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        E-Mail *
                      </label>
                      <input
                        type="email"
                        required
                        value={companyInfo.email}
                        onChange={(e) => handleCompanyInfoChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        required
                        value={companyInfo.phone}
                        onChange={(e) => handleCompanyInfoChange('phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Straße und Hausnummer *
                      </label>
                      <input
                        type="text"
                        required
                        value={companyInfo.address}
                        onChange={(e) => handleCompanyInfoChange('address', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        PLZ *
                      </label>
                      <input
                        type="text"
                        required
                        value={companyInfo.zipCode}
                        onChange={(e) => handleCompanyInfoChange('zipCode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Stadt *
                      </label>
                      <input
                        type="text"
                        required
                        value={companyInfo.city}
                        onChange={(e) => handleCompanyInfoChange('city', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Land *
                      </label>
                      <select
                        value={companyInfo.country}
                        onChange={(e) => handleCompanyInfoChange('country', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Deutschland">Deutschland</option>
                        <option value="Österreich">Österreich</option>
                        <option value="Schweiz">Schweiz</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Shipping Method */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold mb-4">Versandart wählen</h2>
                  
                  <div className="space-y-4">
                    <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:border-blue-500">
                      <input
                        type="radio"
                        name="shipping"
                        value="express"
                        checked={shippingMethod === 'express'}
                        onChange={(e) => setShippingMethod(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <span className="font-medium">Express-Versand</span>
                        <p className="text-sm text-gray-600">5-10 Tage - 25,00 €</p>
                      </div>
                    </label>
                    
                    <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:border-blue-500">
                      <input
                        type="radio"
                        name="shipping"
                        value="standard"
                        checked={shippingMethod === 'standard'}
                        onChange={(e) => setShippingMethod(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <span className="font-medium">Standard-Versand</span>
                        <p className="text-sm text-gray-600">15-30 Tage - 15,00 €</p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Step 3: Payment Method */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold mb-4">Zahlungsart wählen</h2>
                  
                  <div className="space-y-4">
                    <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:border-blue-500">
                      <input
                        type="radio"
                        name="payment"
                        value="bank_transfer"
                        checked={paymentMethod === 'bank_transfer'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <span className="font-medium">Überweisung</span>
                        <p className="text-sm text-gray-600">Zahlung per Banküberweisung</p>
                      </div>
                    </label>
                    
                    <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:border-blue-500">
                      <input
                        type="radio"
                        name="payment"
                        value="paypal"
                        checked={paymentMethod === 'paypal'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <span className="font-medium">PayPal</span>
                        <p className="text-sm text-gray-600">Sicher bezahlen mit PayPal</p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Step 4: Order Review */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold mb-4">Bestellung überprüfen</h2>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold mb-3">Artikelübersicht</h3>
                    {items.map((item) => (
                      <div key={item.id} className="flex justify-between py-2 border-b border-gray-200">
                        <span>{item.name?.de || item.name}</span>
                        <span>{item.quantity} × {item.price.toFixed(2)} €</span>
                      </div>
                    ))}
                    
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between">
                        <span>Zwischensumme:</span>
                        <span>{total.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Versand ({shippingMethod === 'express' ? 'Express' : 'Standard'}):</span>
                        <span>{shippingCost.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-200">
                        <span>Gesamtsumme:</span>
                        <span>{finalTotal.toFixed(2)} €</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold mb-3">Lieferadresse</h3>
                    <p>{companyInfo.name}<br />
                    {companyInfo.contactPerson}<br />
                    {companyInfo.address}<br />
                    {companyInfo.zipCode} {companyInfo.city}<br />
                    {companyInfo.country}</p>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Zurück
                  </button>
                ) : (
                  <div></div>
                )}
                
                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(currentStep + 1)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Weiter
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {isProcessing ? 'Wird verarbeitet...' : 'Bestellung bestätigen'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export default CheckoutPage